import { Countdown } from '../../../components/general/Countdown';
import FloatingCard from '../../../components/general/FloatingCard';
import { DotsLoader } from '../../../components/general/DotsLoader';
import SetMissionNoteDialog from '../../../components/dialogs/SetMissionNoteDialog';
import MissionLogsDialog from '../../../components/dialogs/MissionLogsDialog';
import VehicleModuleVersionsDialog from '../../../components/dialogs/VehicleModuleVersionsDialog';
import ConfirmRerouteFromDockDialog from '../../../components/dialogs/ConfirmRerouteFromDockDialog';
import ConfirmCancelDockMissionDialog from '../../../components/dialogs/ConfirmCancelDockMissionDialog';
import { en } from '../../../locales/en';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useGcsStore } from '../../../store/useGcsStore';
import { MissionStatus } from '../../../types/gcs/mission';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { MenuItemTileProps } from '../../../components/general/MenuItemTile';
import VehiclePanelHeader from './VehiclePanelHeader';
import { MissionStepper } from './MissionStepper';
import { McErrorTile } from './McErrorTile';
import { SecondaryButton } from '../../../components/buttons/SecondaryButton';

interface WaitingOnDockVehiclePanelProps {
  vehicleId: VehicleId;
  gcsId: string;
  onClose: () => void;
  onFocusVehicle: () => void;
}

function WaitingOnDockVehiclePanel({ vehicleId, gcsId, onClose, onFocusVehicle }: WaitingOnDockVehiclePanelProps) {
  const { showDialog, hideDialog } = useDialogQueueStore();

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const untilMs = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return null;
    const detailedState = gcsData.missionDetailedStateMap.get(liveMission.missionId);
    if (detailedState?.status !== MissionStatus.WAITING_ON_DOCK) return null;
    return detailedState.untilMs ?? null;
  });

  const isCalculatingRoute = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return false;
    const dockPreflight = [...gcsData.dockPreflightMissionMap.values()].find(
      (n) => n.dockPreflightInfo?.missionId === liveMission.missionId
    );
    return dockPreflight?.dockPreflightInfo?.isCalculatingRoute ?? false;
  });

  const menuItems: MenuItemTileProps[] = [
    {
      label: en.vehiclePanelMenuItems.showVersions,
      onClick: () => {
        showDialog({
          id: `vehicle-module-versions-${vehicleId}`,
          component: <VehicleModuleVersionsDialog vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionNote,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: 'set-mission-note',
          component: (
            <SetMissionNoteDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={() => useDialogQueueStore.getState().hideDialog()}
            />
          )
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionLogs,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: `mission-logs-dialog-${liveMission.missionId}`,
          component: (
            <MissionLogsDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.cancelMission,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: `confirm-cancel-dock-mission-${liveMission.missionId}`,
          component: (
            <ConfirmCancelDockMissionDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    }
  ];

  return (
    <FloatingCard>
      <VehiclePanelHeader
        vehicleId={vehicleId}
        gcsId={gcsId}
        onFocusVehicle={onFocusVehicle}
        onClose={onClose}
        menuItems={menuItems}
      />
      <McErrorTile gcsId={gcsId} vehicleId={vehicleId} />
      <MissionStepper gcsId={gcsId} vehicleId={vehicleId} />
      <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {untilMs != null && (
          <Countdown
            targetMs={untilMs}
            label={en.waitingOnDockPanel.reroutingHomeAutomatically}
            variant='body2-medium'
          />
        )}
        <SecondaryButton
          text={en.waitingOnDockPanel.rerouteHomeButton}
          callback={() => {
            if (!liveMission) return;
            showDialog({
              id: 'confirm-return-home',
              component: (
                <ConfirmRerouteFromDockDialog
                  gcsId={gcsId}
                  vehicleId={vehicleId}
                  missionId={liveMission.missionId}
                  onClose={() => useDialogQueueStore.getState().hideDialog()}
                />
              )
            });
          }}
        />
        {isCalculatingRoute && <DotsLoader text={en.waitingOnDockPanel.calculatingRoute} />}
      </div>
    </FloatingCard>
  );
}

export default WaitingOnDockVehiclePanel;
